import { useState } from 'react';
import { CURRENCIES, CATEGORIES, INCOME_CATEGORIES, BIZ_CLASS, CLASS_SELECT_KEYS } from '../utils/constants';
import { T } from '../theme';

const today = () => new Date().toISOString().slice(0, 10);

const DIR_OPTIONS = [
  { value: 'debit',  label: 'Debit (money out)' },
  { value: 'credit', label: 'Credit (money in)' },
];

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1.5px solid #E5E7EB', fontSize: 14, outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff',
};
const labelStyle = { fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4, display: 'block' };

function AddTransactionModal({ transactionFiles, defaultCurrency, categories, incomeCategories, onSave, onClose }) {
  const [targetFile, setTargetFile] = useState(transactionFiles[0]?.name ?? '');
  const [form, setForm] = useState({
    vendor: '',
    date: today(),
    amount: '',
    currency: defaultCurrency || 'USD',
    transactionDirection: 'debit',
    businessClassification: 'unclassified_debit',
    category: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const allCategories = form.transactionDirection === 'credit'
    ? (incomeCategories || INCOME_CATEGORIES)
    : (categories || CATEGORIES);

  const handleDirectionChange = (dir) => {
    set('transactionDirection', dir);
    set('businessClassification', dir === 'credit' ? 'unclassified_credit' : 'unclassified_debit');
    set('category', '');
  };

  const canSave = targetFile && form.vendor.trim() && form.date && parseFloat(form.amount) > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(targetFile, {
        vendor: form.vendor.trim(),
        date: form.date,
        amount: parseFloat(form.amount),
        total: parseFloat(form.amount),
        currency: form.currency,
        transactionDirection: form.transactionDirection,
        businessClassification: form.businessClassification || (form.transactionDirection === 'credit' ? 'unclassified_credit' : 'unclassified_debit'),
        ...(form.category    ? { category: form.category }    : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() }  : {}),
        sourceFormat: 'Manual Entry',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1100, backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '24px 20px', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', animation: 'slideUp 0.3s ease-out' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#111' }}>Add Transaction</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#888', padding: 0, lineHeight: 1 }}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Target file */}
          <div>
            <label style={labelStyle}>Add to file</label>
            <select style={inputStyle} value={targetFile} onChange={e => setTargetFile(e.target.value)}>
              {transactionFiles.map(f => (
                <option key={f.name} value={f.name}>
                  {f.accountName ? `${f.accountName} — ` : ''}{f.name}
                </option>
              ))}
            </select>
          </div>

          {/* Vendor */}
          <div>
            <label style={labelStyle}>Vendor *</label>
            <input style={inputStyle} value={form.vendor} onChange={e => set('vendor', e.target.value)} placeholder="e.g. Amazon" autoFocus />
          </div>

          {/* Date + Amount */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Date *</label>
              <input style={inputStyle} type="date" value={form.date} onChange={e => set('date', e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Amount *</label>
              <input style={inputStyle} type="number" step="0.01" min="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00" />
            </div>
          </div>

          {/* Currency + Direction */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Currency</label>
              <select style={inputStyle} value={form.currency} onChange={e => set('currency', e.target.value)}>
                {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Direction</label>
              <select style={inputStyle} value={form.transactionDirection} onChange={e => handleDirectionChange(e.target.value)}>
                {DIR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {/* Classification */}
          <div>
            <label style={labelStyle}>Classification</label>
            <select
              style={{ ...inputStyle, ...(form.businessClassification ? { borderColor: BIZ_CLASS[form.businessClassification]?.color, background: BIZ_CLASS[form.businessClassification]?.bg, color: BIZ_CLASS[form.businessClassification]?.color, fontWeight: 700 } : {}) }}
              value={form.businessClassification}
              onChange={e => set('businessClassification', e.target.value)}
            >
              {CLASS_SELECT_KEYS.map(k => <option key={k} value={k}>{BIZ_CLASS[k].label}</option>)}
            </select>
          </div>

          {/* Category */}
          <div>
            <label style={labelStyle}>Category</label>
            <select style={inputStyle} value={form.category} onChange={e => set('category', e.target.value)}>
              <option value="">— None —</option>
              {allCategories.map(c => {
                const key = typeof c === 'string' ? c : (c.key ?? c);
                const label = typeof c === 'string' ? c : (c.label ?? c.name ?? c);
                return <option key={key} value={key}>{label}</option>;
              })}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label style={labelStyle}>Notes</label>
            <input style={inputStyle} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional" />
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 14, borderRadius: 12, border: '1.5px solid #E5E7EB', background: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', color: '#6B7280' }}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSave || saving}
              style={{ flex: 2, padding: 14, borderRadius: 12, border: 'none', background: canSave && !saving ? T.gradTeal : '#E5E7EB', color: canSave && !saving ? '#fff' : '#9CA3AF', fontSize: 14, fontWeight: 700, cursor: canSave && !saving ? 'pointer' : 'not-allowed', boxShadow: canSave && !saving ? '0 4px 15px rgba(5,196,176,0.3)' : 'none' }}
            >
              {saving ? 'Saving…' : 'Add Transaction'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddTransactionModal;
